import React, {useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {dateTimeFormat, KTSVG} from '../../../../../_metronic/helpers'
import {AuthenticateUser} from '../../../../Utils/_helpers/RoleAndPermission'
import {toast} from 'react-toastify'
import './TemplateMobileCard.scss'

interface TemplateMobileCardProps {
  template: any
  category: string
  isAdmin: boolean
  onDelete: (template: any) => void
  idx: number
}

export const TemplateMobileCard: React.FC<TemplateMobileCardProps> = ({
  template,
  category,
  isAdmin,
  onDelete,
  idx,
}) => {
  const navigate = useNavigate()
  const [showActions, setShowActions] = useState(false)

  const handleActionClick = () => {
    setShowActions(!showActions)
  }

  const templateId = template?._id || template?.id

  return (
    <div className={`template-mobile-card ${showActions ? 'dropdown-open' : ''}`}>
      {/* Header with name and actions */}
      <div className='card-header-section'>
        <div className='d-flex align-items-center gap-3 flex-grow-1'>
          <div className='template-info-section'>
            <h5 className='template-name'>{template?.name || template?.title || '-'}</h5>
            {category !== 'Email' && template?.nameSub && (
              <span className='template-subtitle text-muted'>{template.nameSub}</span>
            )}
          </div>
        </div>
        <button
          className='btn btn-action-mobile'
          onClick={handleActionClick}
          aria-label='Actions menu'
          aria-expanded={showActions}
        >
          <i className='fas fa-ellipsis-v'></i>
        </button>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className='actions-dropdown'>
          {AuthenticateUser('edit-template') && (
            <button
              className='action-item'
              onClick={() => {
                navigate('/New-Template', {
                  state: {
                    id: idx,
                    data: template,
                    category: category,
                  },
                })
                setShowActions(false)
              }}
            >
              <i className='fas fa-edit me-2'></i>
              Edit
            </button>
          )}
          {AuthenticateUser('delete-template') && (
            <button
              className='action-item text-danger'
              onClick={() => {
                onDelete(template)
                setShowActions(false)
              }}
            >
              <i className='fas fa-trash me-2'></i>
              Delete
            </button>
          )}
        </div>
      )}

      {/* Template details */}
      <div className='card-details-grid'>
        {/* ID */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='fas fa-hashtag me-1'></i>
            ID
          </span>
          <span className='detail-value'>
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id={`tooltip-${templateId}`}>{templateId}</Tooltip>}
            >
              <span className='text-truncate d-inline-block' style={{maxWidth: '200px'}}>
                {templateId}
              </span>
            </OverlayTrigger>
            <button
              onClick={() => {
                navigator.clipboard.writeText(templateId)
                toast.success('Copied!')
              }}
              className='btn btn-sm btn-icon btn-outline-light ms-2'
              style={{padding: '2px 4px', minWidth: 'auto'}}
              aria-label='Copy ID'
            >
              <span className='svg-icon svg-icon-2'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    opacity='0.5'
                    d='M18 2H9C7.34315 2 6 3.34315 6 5H8C8 4.44772 8.44772 4 9 4H18C18.5523 4 19 4.44772 19 5V16C19 16.5523 18.5523 17 18 17V19C19.6569 19 21 17.6569 21 16V5C21 3.34315 19.6569 2 18 2Z'
                    fill='currentColor'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M14.7857 7.125H6.21429C5.62255 7.125 5.14286 7.6007 5.14286 8.1875V18.8125C5.14286 19.3993 5.62255 19.875 6.21429 19.875H14.7857C15.3774 19.875 15.8571 19.3993 15.8571 18.8125V8.1875C15.8571 7.6007 15.3774 7.125 14.7857 7.125ZM6.21429 5C4.43908 5 3 6.42709 3 8.1875V18.8125C3 20.5729 4.43909 22 6.21429 22H14.7857C16.5609 22 18 20.5729 18 18.8125V8.1875C18 6.42709 16.5609 5 14.7857 5H6.21429Z'
                    fill='currentColor'
                  />
                </svg>
              </span>
            </button>
          </span>
        </div>

        {/* Type Badge */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='fas fa-tag me-1'></i>
            Type
          </span>
          <span className='detail-value detail-value-with-badge'>
            <span
              className={
                template?.type === 'MMS'
                  ? 'badge badge-light-success green_text_badge badge-square'
                  : 'badge badge-light-info fs-7 blue_text_badge badge-square'
              }
            >
              {template?.type !== 'TEXT' ? template?.type : 'EMAIL'}
            </span>
          </span>
        </div>

        {category === 'Email' && (
          <>
            {template?.subject || template?.emailSubject || template?.subjectLine ? (
              <div className='detail-item full-width'>
                <span className='detail-label'>
                  <i className='fas fa-heading me-1'></i>
                  Subject Line
                </span>
                <span className='detail-value text-truncate' title={template?.subject || template?.emailSubject || template?.subjectLine || '-'}>
                  {template?.subject || template?.emailSubject || template?.subjectLine || '-'}
                </span>
              </div>
            ) : null}
            {template?.previewText || template?.preview ? (
              <div className='detail-item full-width'>
                <span className='detail-label'>
                  <i className='fas fa-eye me-1'></i>
                  Preview Text
                </span>
                <span className='detail-value text-truncate' title={template?.previewText || template?.preview || '-'}>
                  {template?.previewText || template?.preview || '-'}
                </span>
              </div>
            ) : null}
          </>
        )}

        {!isAdmin && category !== 'Email' && (template?.message || template?.content) && (
          <div className='detail-item full-width'>
            <span className='detail-label'>
              <i className='fas fa-align-left me-1'></i>
              Content
            </span>
            <span
              className='detail-value text-truncate'
              style={{fontFamily: 'monospace', fontSize: '0.9rem'}}
              title={template?.message || template?.content}
            >
              {template?.message || template?.content}
            </span>
          </div>
        )}

        {isAdmin && (
          <>
            {template?.scope && (
              <div className='detail-item'>
                <span className='detail-label'>
                  <i className='fas fa-globe me-1'></i>
                  Scope
                </span>
                <span className='detail-value text-truncate' title={template.scope}>{template.scope}</span>
              </div>
            )}
            {template?.category && (
              <div className='detail-item'>
                <span className='detail-label'>
                  <i className='fas fa-folder me-1'></i>
                  Category
                </span>
                <span className='detail-value text-truncate' title={template.category}>{template.category}</span>
              </div>
            )}
            {template?.industry && (
              <div className='detail-item'>
                <span className='detail-label'>
                  <i className='fas fa-industry me-1'></i>
                  Industry
                </span>
                <span className='detail-value text-truncate' title={template.industry}>{template.industry}</span>
              </div>
            )}
            {template?.affiliate && (
              <div className='detail-item'>
                <span className='detail-label'>
                  <i className='fas fa-handshake me-1'></i>
                  Affiliate
                </span>
                <span className='detail-value text-truncate' title={template.affiliate}>{template.affiliate}</span>
              </div>
            )}
          </>
        )}

        {template?.createdAt && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-calendar-plus me-1'></i>
              Created
            </span>
            <span className='detail-value text-muted'>
              {dateTimeFormat(template.createdAt, 'MMM DD YYYY')}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

